import CategoryService from '@/services/category.services.js';
import GoogleMapsService from '@/services/googleMapsService';
import GroupsService from '@/services/group.services.js';
import IndustryService from '@/services/industry.service';
import SubCategoryService from '@/services/sub.category.services.js';
import { getGroupsBySubcategory } from '@/utils/groupData';
import {
  getMockBodyTypes,
  getMockMakeModels,
  getMockRegionalSpecs,
  getMockTrims,
} from '@/utils/mockDataUtils';
import axios from 'axios';

const USE_MOCK_DATA = false;

export default {
  namespaced: true,
  state: {
    categories: [],
    selectedCategory: null,
    subcategories: [],
    selectedSubcategory: null,
    groups: [],
    jobapplicationgroups: [],
    industries: [],
    selectedGroup: null,
    sections: [],
    selectedSection: null,
    listings: [],
    cities: [],
    mockCities: [],
    trims: [],
    regionalSpecs: [],
    bodyTypes: [],
    makeModels: [],
    selectedCity: null,
    selectedLocation: null,
    loadingCities: false,
    loading: false,
    error: null,
  },

  mutations: {
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },

    SET_SELECTED_CATEGORY(state, category) {
      state.selectedCategory = category;
    },

    SET_SUBCATEGORIES(state, subcategories) {
      state.subcategories = subcategories;
    },

    SET_SELECTED_SUBCATEGORY(state, subcategory) {
      state.selectedSubcategory = subcategory;
    },

    SET_GROUPS(state, groups) {
      state.groups = groups;
    },

    SET_JOB_APPLICATION_GROUPS(state, jobapplicationgroups) {
      state.jobapplicationgroups = jobapplicationgroups;
    },

    SET_INDUSTRIES(state, industries) {
      state.industries = industries;
    },

    SET_SELECTED_GROUP(state, group) {
      state.selectedGroup = group;
    },

    SET_SECTIONS(state, sections) {
      state.sections = sections;
    },

    SET_SELECTED_SECTION(state, section) {
      state.selectedSection = section;
    },

    SET_LISTINGS(state, listings) {
      state.listings = listings;
    },

    SET_CITIES(state, cities) {
      state.cities = cities;
    },

    SET_SELECTED_CITY(state, city) {
      state.selectedCity = city;
    },

    SET_SELECTED_LOCATION(state, location) {
      state.selectedLocation = location;
    },

    SET_LOADING_CITIES(state, status) {
      state.loadingCities = status;
    },

    SET_LOADING(state, status) {
      state.loading = status;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    SET_TRIMS(state, trims) {
      state.trims = trims;
    },

    SET_REGIONAL_SPECS(state, specs) {
      state.regionalSpecs = specs;
    },

    SET_BODY_TYPES(state, bodyTypes) {
      state.bodyTypes = bodyTypes;
    },

    SET_MAKE_MODELS(state, makeModels) {
      state.makeModels = makeModels;
    },
  },

  actions: {
    async fetchCategories({ commit }) {
      commit('SET_LOADING', true);
      try {
        let categories;
        const USE_MOCK_DATA_FOR_THIS_ACTION = false;

        if (USE_MOCK_DATA_FOR_THIS_ACTION) {
          categories = [
            { id: 1, name: 'Motors', slug: 'motors' },
            { id: 2, name: 'Property', slug: 'property' },
            { id: 3, name: 'Jobs', slug: 'jobs' },
            { id: 4, name: 'Classifieds', slug: 'classified' },
            { id: 5, name: 'Furniture & Garden', slug: 'furniture-garden' },
            { id: 6, name: 'Mobiles & Tablets', slug: 'mobiles-tablets' },
            { id: 7, name: 'Community', slug: 'community' },
          ];
        } else {
          const response = await CategoryService.index();
          categories = response.data.data;
        }

        commit('SET_CATEGORIES', categories);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching categories:', error);
        commit('SET_ERROR', 'Failed to load categories.');
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchAllListings({ commit }) {
      const { data } = await ListingService.index();
      commit('SET_LISTINGS', data.data);
    },

    selectCategory({ commit }, category) {
      commit('SET_SELECTED_CATEGORY', category);
    },

    async fetchSubCategories({ commit }, categoryId) {
      commit('SET_LOADING', true);
      try {
        let subcategories = [];
        if (USE_MOCK_DATA) {
          const normalizedCategoryId = isNaN(categoryId)
            ? categoryId.toLowerCase()
            : Number(categoryId);
          if (normalizedCategoryId === 1 || normalizedCategoryId === 'motors') {
            subcategories = [
              { id: 101, name: 'Cars' },
              { id: 102, name: 'Motorcycles' },
              { id: 103, name: 'Auto Accessories & Parts' },
              { id: 104, name: 'Heavy Vehicles' },
              { id: 105, name: 'Boats' },
              { id: 106, name: 'Number Plates' },
            ];
          } else if (normalizedCategoryId === 3 || normalizedCategoryId === 'property-for-sale') {
            subcategories = [
              { id: 201, name: 'Residential' },
              { id: 202, name: 'Commercial' },
              { id: 203, name: 'Land' },
              { id: 204, name: 'Multiple Units' },
            ];
          } else if (normalizedCategoryId === 4 || normalizedCategoryId === 'property-for-rent') {
            subcategories = [
              { id: 301, name: 'Residential' },
              { id: 302, name: 'Commercial' },
              { id: 303, name: 'Rooms For Rent' },
              { id: 304, name: 'Monthly Short Term' },
              { id: 305, name: 'Daily Short Term' },
            ];
          } else if (normalizedCategoryId === 2 || normalizedCategoryId === 'jobs') {
            subcategories = [
              { id: 401, name: 'Accounting / Finance' },
              { id: 402, name: 'Automobile' },
              { id: 403, name: 'Beauty / Salon' },
              { id: 404, name: 'Cleaner / Housekeeper' },
              { id: 405, name: 'Construction' },
              { id: 406, name: 'Cook / Chef' },
              { id: 407, name: 'Customer Service / Call Centre' },
              { id: 408, name: 'Data Management & Analysis' },
              { id: 409, name: 'Design' },
              { id: 410, name: 'Driver / Delivery' },
              { id: 411, name: 'Education' },
              { id: 412, name: 'Engineering' },
              { id: 413, name: 'Event Management & Operations' },
              { id: 414, name: 'Handyman / Technician' },
              { id: 415, name: 'HR / Admin' },
              { id: 416, name: 'Information Technology' },
              { id: 417, name: 'Legal Services' },
              { id: 418, name: 'Logistics & Distribution' },
              { id: 419, name: 'Manufacturing / Warehouse' },
              { id: 420, name: 'Marine Captain / Crew' },
              { id: 421, name: 'Marketing / Advertising' },
              { id: 422, name: 'Media, Art & Entertainment' },
              { id: 423, name: 'Medical / Healthcare' },
              { id: 424, name: 'Real Estate' },
              { id: 425, name: 'Restaurant Operations' },
              { id: 426, name: 'Sales / Business Development' },
              { id: 427, name: 'Secretarial / Front Office' },
              { id: 428, name: 'Security / Guard' },
              { id: 429, name: 'Sports & Fitness' },
              { id: 430, name: 'Travel & Hospitality' },
              { id: 431, name: 'Others' },
            ];
          }
        } else {
          const response = await SubCategoryService.getBycategory(categoryId);
          subcategories = response.data.data;
        }
        commit('SET_SUBCATEGORIES', subcategories);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        commit('SET_ERROR', 'Failed to load subcategories.');
      } finally {
        commit('SET_LOADING', false);
      }
    },

    selectSubCategory({ commit }, subcategory) {
      commit('SET_SELECTED_SUBCATEGORY', subcategory);
    },

    async fetchGroups({ commit, state }, subcategoryId) {
      commit('SET_LOADING', true);
      try {
        let groups = [];
        if (USE_MOCK_DATA) {
          groups = getGroupsBySubcategory(subcategoryId, state.cities);
        } else {
          const response = await GroupsService.subCategoryGroups(subcategoryId);
          groups = response.data.data;
        }
        commit('SET_GROUPS', groups);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching groups:', error);
        commit('SET_ERROR', 'Failed to load groups.');
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchJobApplicationGroups({ commit, state }) {
      commit('SET_LOADING', true);
      try {
        let jobapplicationgroups = [];

        const response = await GroupsService.index();
        jobapplicationgroups = response.data.data;

        commit('SET_JOB_APPLICATION_GROUPS', jobapplicationgroups);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching groups:', error);
        commit('SET_ERROR', 'Failed to load groups.');
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchIndustries({ commit, state }) {
      commit('SET_LOADING', true);
      try {
        let industries = [];

        const response = await IndustryService.index();
        industries = response.data.data;

        commit('SET_INDUSTRIES', industries);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching groups:', error);
        commit('SET_ERROR', 'Failed to load groups.');
      } finally {
        commit('SET_LOADING', false);
      }
    },

    selectGroup({ commit }, group) {
      commit('SET_SELECTED_GROUP', group);
    },

    fetchSections({ commit }, groupId) {
      commit('SET_LOADING', true);
      try {
        const sections = [
          { id: 301, name: 'Hyper Sports' },
          { id: 302, name: 'Super Bike' },
        ];
        commit('SET_SECTIONS', sections);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', 'Failed to load sections.');
      } finally {
        commit('SET_LOADING', false);
      }
    },

    selectSection({ commit }, section) {
      commit('SET_SELECTED_SECTION', section);
    },

    clearSelectedCategory({ commit, state }) {
      commit('SET_SELECTED_CATEGORY', null);
      // Also clear subcategories when category is cleared
      commit('SET_SUBCATEGORIES', []);
      commit('SET_SELECTED_SUBCATEGORY', null);
      commit('SET_GROUPS', []);
      commit('SET_SELECTED_GROUP', null);
      commit('SET_SECTIONS', []);
      commit('SET_SELECTED_SECTION', null);
    },

    clearSubcategories({ commit, state }) {
      commit('SET_SUBCATEGORIES', []);
      commit('SET_SELECTED_SUBCATEGORY', null);
      commit('SET_GROUPS', []);
      commit('SET_SELECTED_GROUP', null);
      commit('SET_SECTIONS', []);
      commit('SET_SELECTED_SECTION', null);
    },

    /**
     * Clear all category state
     */
    clearAllCategoryState({ commit }) {
      commit('SET_SELECTED_CATEGORY', null);
      commit('SET_SUBCATEGORIES', []);
      commit('SET_SELECTED_SUBCATEGORY', null);
      commit('SET_GROUPS', []);
      commit('SET_SELECTED_GROUP', null);
      commit('SET_SECTIONS', []);
      commit('SET_SELECTED_SECTION', null);
    },

    async fetchCategoryName({ state }, categoryId) {
      const category = state.categories.find((cat) => cat.id === categoryId);
      if (category) {
        return category.name;
      }

      try {
        return null;
      } catch (error) {
        console.error(`Failed to fetch category name for ID ${categoryId}:`, error);
        return null;
      }
    },

    async fetchSubcategoryName({ state }, subcategoryId) {
      const subcategory = state.subcategories.find((sub) => sub.id === subcategoryId);
      if (subcategory) {
        return subcategory.title || subcategory.name;
      }

      return null;
    },

    async fetchGroupName({ state }, groupId) {
      const group = state.groups.find((grp) => grp.id === groupId);
      if (group) {
        return group.title || group.name;
      }

      return null;
    },

    async fetchSectionName({ state }, sectionId) {
      for (const group of state.groups) {
        if (group.sections) {
          const section = group.sections.find((sec) => sec.id === sectionId);
          if (section) {
            return section.title || section.name;
          }
        }
      }

      return null;
    },

    async fetchMakeModels({ commit }) {
      const USE_MOCK_DATA_FOR_MAKE_MODELS = true;
      try {
        let makeModels;
        if (USE_MOCK_DATA_FOR_MAKE_MODELS) {
          makeModels = getMockMakeModels();
        } else {
          const response = await axios.get('/api/make-models');
          makeModels = response.data;
        }
        const transformedMakeModels = makeModels.map((model) => ({
          name: model.name,
          slug: model.slug,
        }));
        commit('SET_MAKE_MODELS', transformedMakeModels);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching makeModels:', error);
      }
    },

    async fetchCities({ commit }) {
      commit('SET_LOADING_CITIES', true);
      try {
        const response = await GoogleMapsService.fetchCities('Somalia');
        const results = response.data.results || [];
        

        console.log('Fetched cities from Google Maps:', results);

        const cities = results.map((city) => ({
          id: city.place_id,
          cityId: city.id,
          name: city.name || city.description,
          slug: (city.name || city.description).toLowerCase().replace(/\s+/g, '-'),
          lat: city.geometry?.location?.lat || null,
          lng: city.geometry?.location?.lng || null,
        }));

        console.log('Fetched cities:', cities);
        commit('SET_CITIES', cities);
        commit('SET_ERROR', null);
      } catch (error) {
        console.error('Error fetching cities:', error);
        commit('SET_ERROR', 'Failed to load cities.');
      } finally {
        commit('SET_LOADING_CITIES', false);
      }
    },

    async fetchTrims({ commit }) {
      const USE_MOCK_DATA_FOR_TRIMS = true;
      let trims;
      if (USE_MOCK_DATA_FOR_TRIMS) {
        trims = getMockTrims();
      } else {
        const response = await axios.get('/api/trims');
        trims = response.data;
      }
      commit('SET_TRIMS', trims);
    },

    async fetchRegionalSpecs({ commit }) {
      const USE_MOCK_DATA_FOR_REGIONAL_SPECS = true;
      let specs;
      if (USE_MOCK_DATA_FOR_REGIONAL_SPECS) {
        specs = getMockRegionalSpecs();
      } else {
        const response = await axios.get('/api/regional-specs');
        specs = response.data;
      }
      commit('SET_REGIONAL_SPECS', specs);
    },

    async fetchBodyTypes({ commit }) {
      const USE_MOCK_DATA_FOR_BODY_TYPES = true;
      let bodyTypes;
      if (USE_MOCK_DATA_FOR_BODY_TYPES) {
        bodyTypes = getMockBodyTypes();
      } else {
        const response = await axios.get('/api/body-types');
        bodyTypes = response.data;
      }
      commit('SET_BODY_TYPES', bodyTypes);
    },

    selectCity({ commit }, city) {
      if (city && typeof city === 'object') {
        commit('SET_SELECTED_CITY', city);
      } else {
        console.warn('Invalid city object passed to selectCity action:', city);
      }
    },

    selectLocation({ commit }, location) {
      commit('SET_SELECTED_LOCATION', location);
    },
  },

  getters: {
    categories: (state) => state.categories,
    selectedCategory: (state) => state.selectedCategory,
    subcategories: (state) => state.subcategories,
    makeModels: (state) => state.makeModels,
    selectedSubCategory: (state) => state.selectedSubCategory,
    groups: (state) => state.groups,
    jobapplicationgroups: (state) => state.jobapplicationgroups,
    industries: (state) => state.industries,
    selectedGroup: (state) => state.selectedGroup,
    sections: (state) => state.sections,
    selectedSection: (state) => state.selectedSection,
    listings: (state) => state.listings,
    cities: (state) => state.cities,
    mockCities: (state) => state.mockCities,
    selectedCity: (state) => state.selectedCity,
    selectedLocation: (state) => state.selectedLocation,
    isLoadingCities: (state) => state.loadingCities,
    trims: (state) => state.trims,
    regionalSpecs: (state) => state.regionalSpecs,
    bodyTypes: (state) => state.bodyTypes,
    isLoading: (state) => state.loading,
    errorMessage: (state) => state.error,
  },
};
